import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import {
  AssetView,
  AssetStatus,
  AssetCondition,
  AssetMaintenanceView,
  MaintenanceType,
  CreateMaintenanceInput,
  DepreciationScheduleView,
  getMaintenanceHistory,
  createMaintenance,
  getDepreciationSchedule,
} from "../../../../api/General/assets";

type TabId = "details" | "maintenance" | "depreciation";

interface AssetDetailsModalProps {
  open: boolean;
  onClose: () => void;
  asset: AssetView | null;
  onEdit: (asset: AssetView) => void;
  onArchive: (id: number) => void;
  onIssue: (asset: AssetView) => void;
  onReturn: (asset: AssetView) => void;
}

const statusStyles: Record<AssetStatus, string> = {
  [AssetStatus.Available]: "bg-green-100 text-green-800",
  [AssetStatus.Issued]: "bg-blue-100 text-blue-800",
  [AssetStatus.UnderMaintenance]: "bg-yellow-100 text-yellow-800",
  [AssetStatus.Retired]: "bg-muted text-muted-foreground",
};

const conditionStyles: Record<AssetCondition, string> = {
  [AssetCondition.Excellent]: "bg-emerald-100 text-emerald-800",
  [AssetCondition.Good]: "bg-blue-100 text-blue-800",
  [AssetCondition.Fair]: "bg-orange-100 text-orange-800",
  [AssetCondition.Poor]: "bg-red-100 text-red-800",
};

const AssetDetailsModal: React.FC<AssetDetailsModalProps> = ({
  open,
  onClose,
  asset,
  onEdit,
  onArchive,
  onIssue,
  onReturn,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>("details");

  // Maintenance state
  const [maintenanceRecords, setMaintenanceRecords] = useState<AssetMaintenanceView[]>([]);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState<Omit<CreateMaintenanceInput, "assetId">>({
    type: MaintenanceType.Cleaning,
    maintenanceDate: new Date().toISOString().split("T")[0],
    cost: 0,
    vendor: "",
    performedBy: "",
    notes: "",
  });

  // Depreciation state
  const [depreciationData, setDepreciationData] = useState<DepreciationScheduleView | null>(null);
  const [depreciationLoading, setDepreciationLoading] = useState(false);
  const [usefulLife, setUsefulLife] = useState(5);

  // Reset state when modal opens/closes or asset changes
  useEffect(() => {
    if (open) {
      setActiveTab("details");
      setMaintenanceRecords([]);
      setDepreciationData(null);
      setShowMaintenanceForm(false);
    }
  }, [open, asset?.id]);

  const loadMaintenance = useCallback(async () => {
    if (!asset) return;
    setMaintenanceLoading(true);
    try {
      const result = await getMaintenanceHistory(asset.id);
      if (result.errors.length === 0) {
        setMaintenanceRecords(result.payload);
      }
    } catch {
      // silent
    } finally {
      setMaintenanceLoading(false);
    }
  }, [asset]);

  const loadDepreciation = useCallback(async () => {
    if (!asset) return;
    setDepreciationLoading(true);
    try {
      const result = await getDepreciationSchedule(asset.id, usefulLife);
      if (result.errors.length === 0) {
        setDepreciationData(result.payload);
      } else {
        setDepreciationData(null);
      }
    } catch {
      // silent
    } finally {
      setDepreciationLoading(false);
    }
  }, [asset, usefulLife]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === "maintenance" && maintenanceRecords.length === 0) {
      loadMaintenance();
    }
    if (activeTab === "depreciation" && !depreciationData) {
      loadDepreciation();
    }
  }, [activeTab, loadMaintenance, loadDepreciation, maintenanceRecords.length, depreciationData]);

  if (!asset) return null;

  const statusLabel = (s: AssetStatus) => {
    switch (s) {
      case AssetStatus.Available: return t("assets.statusAvailable");
      case AssetStatus.Issued: return t("assets.statusIssued");
      case AssetStatus.UnderMaintenance: return t("assets.statusUnderMaintenance");
      case AssetStatus.Retired: return t("assets.statusRetired");
      default: return "";
    }
  };

  const conditionLabel = (c: AssetCondition) => {
    switch (c) {
      case AssetCondition.Excellent: return t("assets.conditionExcellent");
      case AssetCondition.Good: return t("assets.conditionGood");
      case AssetCondition.Fair: return t("assets.conditionFair");
      case AssetCondition.Poor: return t("assets.conditionPoor");
      default: return "";
    }
  };

  const maintenanceTypeLabel = (type: MaintenanceType) => {
    const labels: Record<MaintenanceType, string> = {
      [MaintenanceType.Cleaning]: t("assets.maint.typeCleaning"),
      [MaintenanceType.Repair]: t("assets.maint.typeRepair"),
      [MaintenanceType.Replacement]: t("assets.maint.typeReplacement"),
      [MaintenanceType.Inspection]: t("assets.maint.typeInspection"),
      [MaintenanceType.Upgrade]: t("assets.maint.typeUpgrade"),
      [MaintenanceType.Calibration]: t("assets.maint.typeCalibration"),
      [MaintenanceType.PreventiveMaintenance]: t("assets.maint.typePreventive"),
      [MaintenanceType.Other]: t("assets.maint.typeOther"),
    };
    return labels[type] ?? "";
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (value: number, currency?: string) => {
    const formatted = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return currency ? `${formatted} ${currency}` : formatted;
  };

  const handleSaveMaintenance = async () => {
    setSavingMaintenance(true);
    try {
      const result = await createMaintenance({
        ...maintenanceForm,
        assetId: asset.id,
      });
      if (result.errors.length === 0) {
        setShowMaintenanceForm(false);
        setMaintenanceForm({
          type: MaintenanceType.Cleaning,
          maintenanceDate: new Date().toISOString().split("T")[0],
          cost: 0,
          vendor: "",
          performedBy: "",
          notes: "",
        });
        await loadMaintenance();
      }
    } catch {
      // silent
    } finally {
      setSavingMaintenance(false);
    }
  };

  const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );

  const tabs: { id: TabId; label: string }[] = [
    { id: "details", label: t("assets.tabDetails") },
    { id: "maintenance", label: t("assets.tabMaintenance") },
    { id: "depreciation", label: t("assets.tabDepreciation") },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{asset.name}</DialogTitle>
          <p className="text-sm text-muted-foreground">{asset.categoryName}</p>
        </DialogHeader>

        <DialogBody>
          {/* Tab Navigation */}
          <div className="border-b border-border mb-6">
            <nav className="-mb-px flex space-x-6 rtl:space-x-reverse overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`pb-3 text-sm font-medium border-b-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    activeTab === tab.id
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Details Tab */}
          {activeTab === "details" && (
            <>
              {/* Status overview */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-muted rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">{t("assets.status")}</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[asset.status]}`}>
                    {statusLabel(asset.status)}
                  </span>
                </div>
                <div className="bg-muted rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">{t("assets.condition")}</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${conditionStyles[asset.condition]}`}>
                    {conditionLabel(asset.condition)}
                  </span>
                </div>
                <div className="bg-muted rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">{t("assets.value")}</p>
                  <p className="text-sm font-semibold text-foreground">
                    {asset.purchasePrice != null
                      ? formatCurrency(asset.purchasePrice)
                      : "-"}
                  </p>
                </div>
                <div className="bg-muted rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">{t("assets.assignedTo")}</p>
                  <p className="text-sm font-semibold text-foreground">{asset.assignedToUserName ?? "-"}</p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-0">
                <InfoRow label={t("assets.serialNumber")} value={
                  asset.serialNumber
                    ? <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">{asset.serialNumber}</code>
                    : "-"
                } />
                <InfoRow label={t("assets.category")} value={asset.categoryName ?? "-"} />
                <InfoRow label={t("assets.location")} value={asset.location ?? "-"} />
                <InfoRow label={t("assets.purchaseDate")} value={formatDate(asset.purchaseDate)} />
                <InfoRow label={t("assets.warrantyExpiry")} value={formatDate(asset.warrantyExpiry)} />
                {asset.notes && (
                  <div className="pt-3">
                    <p className="text-sm text-muted-foreground mb-1">{t("assets.notes")}</p>
                    <p className="text-sm text-foreground bg-muted rounded-lg p-3">{asset.notes}</p>
                  </div>
                )}
              </div>

              {/* Issue/Return Actions */}
              {asset.status === AssetStatus.Available && (
                <div className="mt-4">
                  <Button
                    variant="success"
                    fullWidth
                    onClick={() => onIssue(asset)}
                    data-testid="asset-issue-btn"
                  >
                    <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    {t("assets.issueAsset")}
                  </Button>
                </div>
              )}
              {asset.status === AssetStatus.Issued && (
                <div className="mt-4">
                  <Button
                    variant="warning"
                    fullWidth
                    onClick={() => onReturn(asset)}
                    data-testid="asset-return-btn"
                  >
                    <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    {t("assets.returnAsset")}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Maintenance Tab */}
          {activeTab === "maintenance" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">{t("assets.maint.history")}</h3>
                <Button
                  size="xs"
                  onClick={() => setShowMaintenanceForm(!showMaintenanceForm)}
                >
                  <svg className="w-3.5 h-3.5 me-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t("assets.maint.addRecord")}
                </Button>
              </div>

              {/* Add Maintenance Form */}
              {showMaintenanceForm && (
                <div className="bg-muted rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">{t("assets.maint.type")}</label>
                      <select
                        value={maintenanceForm.type}
                        onChange={(e) => setMaintenanceForm({ ...maintenanceForm, type: e.target.value as MaintenanceType })}
                        className="w-full rounded-lg border-border text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      >
                        {Object.values(MaintenanceType)
                          .map((v) => (
                            <option key={v} value={v}>{maintenanceTypeLabel(v)}</option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">{t("assets.maint.date")}</label>
                      <input
                        type="date"
                        value={maintenanceForm.maintenanceDate}
                        onChange={(e) => setMaintenanceForm({ ...maintenanceForm, maintenanceDate: e.target.value })}
                        className="w-full rounded-lg border-border text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">{t("assets.maint.cost")}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={maintenanceForm.cost}
                        onChange={(e) => setMaintenanceForm({ ...maintenanceForm, cost: parseFloat(e.target.value) || 0 })}
                        className="w-full rounded-lg border-border text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">{t("assets.maint.vendor")}</label>
                      <input
                        type="text"
                        value={maintenanceForm.vendor}
                        onChange={(e) => setMaintenanceForm({ ...maintenanceForm, vendor: e.target.value })}
                        className="w-full rounded-lg border-border text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        placeholder={t("assets.maint.vendorPlaceholder")}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">{t("assets.maint.performedBy")}</label>
                      <input
                        type="text"
                        value={maintenanceForm.performedBy}
                        onChange={(e) => setMaintenanceForm({ ...maintenanceForm, performedBy: e.target.value })}
                        className="w-full rounded-lg border-border text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        placeholder={t("assets.maint.performedByPlaceholder")}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">{t("assets.notes")}</label>
                    <textarea
                      value={maintenanceForm.notes}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, notes: e.target.value })}
                      rows={2}
                      className="w-full rounded-lg border-border text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                      placeholder={t("assets.maint.notesPlaceholder")}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => setShowMaintenanceForm(false)}
                    >
                      {t("common.close")}
                    </Button>
                    <Button
                      size="xs"
                      onClick={handleSaveMaintenance}
                      loading={savingMaintenance}
                    >
                      {savingMaintenance ? t("assets.maint.saving") : t("assets.maint.save")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Maintenance Table */}
              {maintenanceLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </div>
              ) : maintenanceRecords.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-10 w-10 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="mt-2 text-sm text-muted-foreground">{t("assets.maint.noRecords")}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground uppercase">{t("assets.maint.date")}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground uppercase">{t("assets.maint.type")}</th>
                        <th className="px-3 py-2 text-end text-xs font-medium text-muted-foreground uppercase">{t("assets.maint.cost")}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground uppercase">{t("assets.maint.vendor")}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground uppercase">{t("assets.maint.performedBy")}</th>
                        <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground uppercase">{t("assets.notes")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {maintenanceRecords.map((record) => (
                        <tr key={record.id} className="hover:bg-muted">
                          <td className="px-3 py-2.5 text-foreground whitespace-nowrap">{formatDate(record.maintenanceDate)}</td>
                          <td className="px-3 py-2.5 text-foreground">{maintenanceTypeLabel(record.type)}</td>
                          <td className="px-3 py-2.5 text-foreground text-end whitespace-nowrap">
                            {record.cost > 0 ? formatCurrency(record.cost) : t("assets.maint.free")}
                          </td>
                          <td className="px-3 py-2.5 text-foreground">{record.vendor ?? "-"}</td>
                          <td className="px-3 py-2.5 text-foreground">{record.performedBy ?? "-"}</td>
                          <td className="px-3 py-2.5 text-muted-foreground text-xs max-w-[120px] truncate">{record.notes ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Depreciation Tab */}
          {activeTab === "depreciation" && (
            <div className="space-y-4">
              {/* Useful Life Selector */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-foreground">{t("assets.depr.usefulLife")}</label>
                <select
                  value={usefulLife}
                  onChange={(e) => {
                    setUsefulLife(Number(e.target.value));
                    setDepreciationData(null);
                  }}
                  className="rounded-lg border-border text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                >
                  {[1, 2, 3, 4, 5, 7, 10, 15, 20].map((y) => (
                    <option key={y} value={y}>
                      {y} {t("assets.depr.years")}
                    </option>
                  ))}
                </select>
              </div>

              {depreciationLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </div>
              ) : !depreciationData ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-10 w-10 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-muted-foreground">{t("assets.depr.noData")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("assets.depr.noDataHint")}</p>
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-primary/10 rounded-lg p-3">
                      <p className="text-xs text-primary mb-0.5">{t("assets.depr.originalValue")}</p>
                      <p className="text-sm font-semibold text-primary">
                        {formatCurrency(depreciationData.originalValue, depreciationData.currency ?? undefined)}
                      </p>
                    </div>
                    <div className="bg-success/10 rounded-lg p-3">
                      <p className="text-xs text-success mb-0.5">{t("assets.depr.bookValue")}</p>
                      <p className="text-sm font-semibold text-success">
                        {formatCurrency(depreciationData.currentBookValue, depreciationData.currency ?? undefined)}
                      </p>
                    </div>
                    <div className="bg-warning/10 rounded-lg p-3">
                      <p className="text-xs text-warning mb-0.5">{t("assets.depr.totalDepreciation")}</p>
                      <p className="text-sm font-semibold text-warning">
                        {formatCurrency(depreciationData.totalDepreciation, depreciationData.currency ?? undefined)}
                      </p>
                    </div>
                  </div>

                  {/* Formula */}
                  <div className="bg-muted rounded-lg p-3 text-xs text-muted-foreground">
                    <span className="font-medium">{t("assets.depr.method")}:</span>{" "}
                    {t("assets.depr.straightLine")} &mdash;{" "}
                    {t("assets.depr.formula", {
                      original: formatCurrency(depreciationData.originalValue),
                      years: depreciationData.usefulLifeYears,
                      annual: formatCurrency(depreciationData.originalValue / depreciationData.usefulLifeYears),
                    })}
                  </div>

                  {/* Depreciation Schedule Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted">
                          <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground uppercase">{t("assets.depr.year")}</th>
                          <th className="px-3 py-2 text-end text-xs font-medium text-muted-foreground uppercase">{t("assets.depr.expense")}</th>
                          <th className="px-3 py-2 text-end text-xs font-medium text-muted-foreground uppercase">{t("assets.depr.accumulated")}</th>
                          <th className="px-3 py-2 text-end text-xs font-medium text-muted-foreground uppercase">{t("assets.depr.bookValue")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {depreciationData.schedule.map((row) => (
                          <tr key={row.year} className={row.isCurrent ? "bg-primary/5" : "hover:bg-muted"}>
                            <td className="px-3 py-2.5 text-foreground whitespace-nowrap">
                              {t("assets.depr.yearN", { n: row.yearNumber })} ({row.year})
                              {row.isCurrent && (
                                <span className="ms-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/20 text-primary">
                                  {t("assets.depr.current")}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-foreground text-end whitespace-nowrap">
                              {formatCurrency(row.depreciationExpense, depreciationData.currency ?? undefined)}
                            </td>
                            <td className="px-3 py-2.5 text-foreground text-end whitespace-nowrap">
                              {formatCurrency(row.accumulatedDepreciation, depreciationData.currency ?? undefined)}
                            </td>
                            <td className="px-3 py-2.5 text-foreground text-end whitespace-nowrap font-medium">
                              {formatCurrency(row.bookValue, depreciationData.currency ?? undefined)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogBody>

        {/* Actions */}
        <DialogFooter className="justify-between">
          <Button
            variant="outline"
            className="text-destructive border-destructive/30 hover:bg-destructive/10"
            onClick={() => onArchive(asset.id)}
            data-testid="asset-archive-btn"
          >
            {t("assets.archive")}
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              {t("common.close")}
            </Button>
            <Button onClick={() => onEdit(asset)} data-testid="asset-edit-btn">
              {t("common.edit")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssetDetailsModal;
